/**
 * Category Experience — extensión de Category para presentación en storefront.
 * No es entidad nueva: vive en sm_categories.experience (JSONB).
 */
import type { BusinessProfile } from "@/lib/business-profile";

export type CategoryExperienceBadge = "nuevo" | "oferta" | "popular" | "exclusivo";

export interface CategoryExperienceNav {
  header: boolean;
  megaMenu: boolean;
  home: boolean;
  sidebar: boolean;
  mobile: boolean;
  footer: boolean;
}

export interface CategoryExperience {
  showOnHome: boolean;
  featured: boolean;
  homeSortOrder: number;
  showProductCount: boolean;
  accentColor: string | null;
  badge: CategoryExperienceBadge | null;
  nav: CategoryExperienceNav;
}

export const DEFAULT_CATEGORY_EXPERIENCE: CategoryExperience = {
  showOnHome: true,
  featured: false,
  homeSortOrder: 0,
  showProductCount: true,
  accentColor: null,
  badge: null,
  nav: {
    header: true,
    megaMenu: true,
    home: true,
    sidebar: true,
    mobile: true,
    footer: false,
  },
};

const BADGES = new Set<string>(["nuevo", "oferta", "popular", "exclusivo"]);

/** Perfiles cuya Experience navega por categorías (Home / mega / sidebar). */
const CATEGORY_NAV_PROFILES = new Set<string>([
  "market",
  "dietetics",
  "pharmacy",
  "fashion",
  "bazar",
  "pet",
  "restaurant",
  "fast_food",
  "cafe",
  "catalog",
  "digital_products",
]);

const ACCENT_OVERRIDE_PROFILES = new Set<string>([
  "fashion",
  "bazar",
  "pet",
  "pharmacy",
  "cafe",
  "dietetics",
]);

export function businessProfileUsesCategoryNavigation(
  profile: string | BusinessProfile | null | undefined,
): boolean {
  return CATEGORY_NAV_PROFILES.has(String(profile || "catalog"));
}

export function businessProfileSupportsCategoryAccent(
  profile: string | BusinessProfile | null | undefined,
): boolean {
  return ACCENT_OVERRIDE_PROFILES.has(String(profile || "catalog"));
}

export function normalizeCategoryExperience(
  raw: unknown,
  fallbackSortOrder = 0,
): CategoryExperience {
  const src = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const navRaw = src.nav && typeof src.nav === "object" ? (src.nav as Record<string, unknown>) : {};
  const badge = typeof src.badge === "string" && BADGES.has(src.badge) ? (src.badge as CategoryExperienceBadge) : null;
  return {
    showOnHome: src.showOnHome !== false,
    featured: src.featured === true,
    homeSortOrder:
      typeof src.homeSortOrder === "number" && Number.isFinite(src.homeSortOrder)
        ? src.homeSortOrder
        : fallbackSortOrder,
    showProductCount: src.showProductCount !== false,
    accentColor:
      typeof src.accentColor === "string" && src.accentColor.trim()
        ? src.accentColor.trim()
        : null,
    badge,
    nav: {
      header: navRaw.header !== false,
      megaMenu: navRaw.megaMenu !== false,
      home: navRaw.home !== false,
      sidebar: navRaw.sidebar !== false,
      mobile: navRaw.mobile !== false,
      footer: navRaw.footer === true,
    },
  };
}

export type CategoryImageProduct = {
  categoriaId: string | null;
  imagenUrl: string | null;
  tags?: string[];
  destacado?: boolean;
};

export type ResolvedCategoryVisual =
  | { kind: "image"; src: string }
  | { kind: "mosaic"; srcs: string[] };

/**
 * Prioridad: imagen de categoría → producto destacado → primer producto con imagen → mosaic.
 * Nunca devuelve vacío si hay productos con imagen.
 */
export function resolveCategoryVisual(input: {
  categoryImageUrl: string | null | undefined;
  categoryId: string;
  products: CategoryImageProduct[];
}): ResolvedCategoryVisual | null {
  const configured = input.categoryImageUrl?.trim();
  if (configured) return { kind: "image", src: configured };

  const inCat = input.products.filter(
    (p) => p.categoriaId === input.categoryId && Boolean(p.imagenUrl?.trim()),
  );
  if (inCat.length === 0) return null;

  const featured = inCat.find(
    (p) => p.destacado === true || (p.tags ?? []).some((t) => /destacad|featured|hero/i.test(t)),
  );
  if (featured?.imagenUrl) return { kind: "image", src: featured.imagenUrl.trim() };

  if (inCat.length === 1) return { kind: "image", src: inCat[0].imagenUrl!.trim() };

  const srcs = [...new Set(inCat.map((p) => p.imagenUrl!.trim()))].slice(0, 4);
  if (srcs.length >= 2) return { kind: "mosaic", srcs };
  return { kind: "image", src: srcs[0]! };
}

/** URL simple para contextos que solo aceptan un string (og, etc.). */
export function resolveCategoryImageUrl(input: {
  categoryImageUrl: string | null | undefined;
  categoryId: string;
  products: CategoryImageProduct[];
}): string | null {
  const visual = resolveCategoryVisual(input);
  if (!visual) return null;
  return visual.kind === "image" ? visual.src : visual.srcs[0] ?? null;
}

export function sortCategoriesForHome<T extends { experience: CategoryExperience; sortOrder: number; name: string }>(
  cats: T[],
): T[] {
  return [...cats]
    .filter((c) => c.experience.showOnHome && c.experience.nav.home)
    .sort((a, b) => {
      if (a.experience.featured !== b.experience.featured) return a.experience.featured ? -1 : 1;
      if (a.experience.homeSortOrder !== b.experience.homeSortOrder) {
        return a.experience.homeSortOrder - b.experience.homeSortOrder;
      }
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.name.localeCompare(b.name, "es");
    });
}

export function filterCategoriesForNav<T extends { experience: CategoryExperience }>(
  cats: T[],
  surface: keyof CategoryExperienceNav,
): T[] {
  return cats.filter((c) => c.experience.nav[surface]);
}
